import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useNotifications } from '../contexts/NotificationContext';
import authService from '../services/authService';
import { canAccessPath, canSeeOrganizationSettings } from '../config/routePermissions';
import { routeForNotification, formatNotificationTime } from '../utils/notificationUtils';
import { getHeaderBackTarget } from '../utils/backNavigation';
import { shouldShowHeaderBack } from '../utils/headerBack';
import useOpenOverlay from '../utils/useOpenOverlay';
import {
  Header,
  HeaderName,
  HeaderMenuButton,
  HeaderGlobalBar,
  HeaderGlobalAction,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  Content,
  Theme
} from '@carbon/react';
import { 
  Home,
  UserMultiple,
  Time,
  Grid,
  NotificationNew,
  Building,
  Restaurant,
  Hotel,
  Task,
  View,
  Close,
  User,
  Help,
  Blog,
  CalendarHeatMap
} from '@carbon/icons-react';
import logo from '../assets/logo.png';
import ChristmasLights from './ChristmasLights';
import SnowEffect from './SnowEffect';
import ScrollToTopButton from './ScrollToTopButton';
import GlobalSearch from './GlobalSearch';
import AccountMenu from './AccountMenu';
import HeaderBackButton from './HeaderBackButton';
import AndroidBackGuard from './AndroidBackGuard';
import './Shell.css';

const Shell = () => {
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(window.innerWidth >= 1056);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1056);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isNotificationPanelClosing, setIsNotificationPanelClosing] = useState(false);
  const [christmasLightsEnabled, setChristmasLightsEnabled] = useState(() => {
    const saved = localStorage.getItem('christmasLightsEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isChristmasFadingOut, setIsChristmasFadingOut] = useState(false);
  const [showChristmasDecor, setShowChristmasDecor] = useState(() => {
    const saved = localStorage.getItem('christmasLightsEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const {
    notifications,
    unreadCount: unreadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  // Da li je otvoren neki side panel / modal preko celog ekrana - koristi ga
  // „nazad" strelica koja na mobilnom prikazu zamenjuje hamburger.
  const isOverlayOpen = useOpenOverlay();

  // Cilj strelice „nazad" u zaglavlju za trenutnu stranicu (npr. lista
  // rezidenata/zaposlenih), ili `null` kada stranica nije „detalj". Na mobilnom
  // prikazu tada strelica zamenjuje hamburger (vidi render ispod); na desktopu
  // u zaglavlju uvek ostaje hamburger.
  const headerBackTarget = getHeaderBackTarget(location.pathname);
  const userRole = authService.getUserRole();
  const { canAny, permissions, isAdmin, loading: permissionsLoading } = usePermissions();

  // Meni prikazujemo tek kada znamo da je korisnik prijavljen (authLoading) i
  // koje dozvole ima (permissionsLoading). Dok traje bilo koje od ta dva stanja,
  // u sidebaru stoji placeholder umesto polupraznog menija (izbegava FOUC).
  const sidenavLoading = authLoading || permissionsLoading;

  // Da li korisnik sme da vidi stavku menija za datu putanju.
  // `canAccessPath` pokriva i any-of listu ključeva i prefiksna pravila
  // (npr. „Zadaci" -> mora imati bar jednu `tasks.*` dozvolu).
  const canSee = useCallback(
    (path) => canAccessPath(path, { canAny, permissions, isAdmin }),
    [canAny, permissions, isAdmin]
  );

  // Role-specific tasks page (caregiver / physiotherapist / nurse-caretaker)
  const tasksPath =
    userRole === 'physiotherapist'
      ? '/physiotherapist-tasks'
      : userRole === 'nurse' || userRole === 'caretaker'
      ? '/nurse-tasks'
      : '/caregiver-tasks';

  // Negovateljske role: negovatelj, fizioterapeut, sestra i „caretaker".
  const isCareRole =
    userRole === 'caregiver' ||
    userRole === 'physiotherapist' ||
    userRole === 'nurse' ||
    userRole === 'caretaker';

  // Stavke „doma" (Korisnici, Sobe) vide administrator i negovateljske role.
  const canSeeHomeItems = userRole === 'administrator' || isCareRole;

  // Swipe/Drag gesture detection (for both touch and mouse)
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Minimum swipe/drag distance (in px)
  const minSwipeDistance = 50;

  // Touch events
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isLeftSwipe = distance < -minSwipeDistance;
    const isRightSwipe = distance > minSwipeDistance;

    // Swipe from left edge to right - open sidebar
    if (isRightSwipe && touchStart < 50 && !isSideNavExpanded) {
      setIsSideNavExpanded(true);
    }
    // Swipe from right to left - close sidebar
    if (isLeftSwipe && isSideNavExpanded) {
      setIsSideNavExpanded(false);
    }
  };

  // Mouse events (for desktop)
  const onMouseDown = (e) => {
    if (isDesktop) {
      setIsDragging(true);
      setTouchEnd(null);
      setTouchStart(e.clientX);
    }
  };

  const onMouseMove = (e) => {
    if (isDragging && isDesktop) {
      setTouchEnd(e.clientX);
    }
  };

  const onMouseUp = () => {
    if (!isDragging || !isDesktop) return;
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isLeftDrag = distance < -minSwipeDistance;
    const isRightDrag = distance > minSwipeDistance;

    // Drag from left edge to right - open sidebar
    if (isRightDrag && touchStart < 50 && !isSideNavExpanded) {
      setIsSideNavExpanded(true);
    }
    // Drag from right to left - close sidebar
    if (isLeftDrag && isSideNavExpanded) {
      setIsSideNavExpanded(false);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Track window size for desktop/mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1056);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for Christmas lights settings changes
  useEffect(() => {
    const handleChristmasLightsChange = (event) => {
      const newValue = event.detail;
      setChristmasLightsEnabled(newValue);

      if (!newValue) {
        // Start fade out animation
        setIsChristmasFadingOut(true);
        setTimeout(() => {
          setShowChristmasDecor(false);
          setIsChristmasFadingOut(false);
        }, 800); // Match CSS animation duration
      } else {
        // Show immediately when enabling
        setShowChristmasDecor(true);
      }
    };

    window.addEventListener('christmasLightsChanged', handleChristmasLightsChange);
    return () => window.removeEventListener('christmasLightsChanged', handleChristmasLightsChange);
  }, []);

  // Close side nav on route change (mobile only)
  useEffect(() => {
    if (window.innerWidth < 1056) {
      setIsSideNavExpanded(false);
    }
  }, [location.pathname]);

  // Zatvori panel obaveštenja (sa animacijom) - koristi se kada korisnik klikne
  // na hamburger ili na logo u headeru, da panel ne ostane otvoren na mobilnom.
  const closeNotificationPanel = () => {
    if (!isNotificationPanelOpen) return;
    setIsNotificationPanelClosing(true);
    setTimeout(() => {
      setIsNotificationPanelOpen(false);
      setIsNotificationPanelClosing(false);
    }, 300);
  };

  const toggleSideNav = () => {
    // Zatvori panel obaveštenja pre otvaranja/zatvaranja navigacije
    closeNotificationPanel();
    // If we're about to collapse, close all open menus first
    if (isSideNavExpanded) {
      const openMenus = document.querySelectorAll('.cds--side-nav__submenu[aria-expanded="true"]');
      openMenus.forEach(menu => menu.click());
    }
    setIsSideNavExpanded(!isSideNavExpanded);
  };

  const handleSideNavClick = (path) => {
    navigate(path);
    if (window.innerWidth < 1056) {
      setIsSideNavExpanded(false);
    }
  };

  // Handler for clicking on a menu group when sidebar is collapsed
  const handleMenuClick = (e, menuTitle) => {
    if (!isSideNavExpanded && window.innerWidth >= 1056) {
      e.preventDefault();
      e.stopPropagation();

      // First expand the sidebar
      setIsSideNavExpanded(true);

      // Then expand the clicked menu after a short delay
      setTimeout(() => {
        const allMenuButtons = document.querySelectorAll('button.cds--side-nav__submenu');
        allMenuButtons.forEach(btn => {
          const titleSpan = btn.querySelector('.cds--side-nav__submenu-title');
          if (titleSpan && titleSpan.textContent === menuTitle) {
            btn.click();
          }
        });
      }, 200);
    }
  };

  // Add click listeners for collapsed menu icons
  useEffect(() => {
    const handleCollapsedMenuClick = (e) => {
      if (!isSideNavExpanded && window.innerWidth >= 1056) {
        const submenuButton = e.target.closest('.cds--side-nav__submenu');
        if (submenuButton) {
          e.preventDefault();
          e.stopPropagation();

          const titleSpan = submenuButton.querySelector('.cds--side-nav__submenu-title');
          const menuTitle = titleSpan ? titleSpan.textContent : '';

          // Expand sidebar
          setIsSideNavExpanded(true);

          // Expand the menu after sidebar expands
          setTimeout(() => {
            const allMenuButtons = document.querySelectorAll('button.cds--side-nav__submenu');
            allMenuButtons.forEach(btn => {
              const span = btn.querySelector('.cds--side-nav__submenu-title');
              if (span && span.textContent === menuTitle) {
                btn.click();
              }
            });
          }, 200);
        }
      }
    };

    const sideNav = document.querySelector('.cds--side-nav');
    if (sideNav) {
      sideNav.addEventListener('click', handleCollapsedMenuClick, true);
    }

    return () => {
      if (sideNav) {
        sideNav.removeEventListener('click', handleCollapsedMenuClick, true);
      }
    };
  }, [isSideNavExpanded]);

  const handleNotificationsClick = () => {
    if (isNotificationPanelOpen) {
      setIsNotificationPanelClosing(true);
      setTimeout(() => {
        setIsNotificationPanelOpen(false);
        setIsNotificationPanelClosing(false);
      }, 300);
    } else {
      setIsNotificationPanelOpen(true);
    }
  };

  const handleMarkAsRead = (id) => {
    markAsRead(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleDeleteNotification = (id) => {
    deleteNotification(id);
  };

  // Deep-link: navigate to the related entity and mark the notification read.
  const handleNotificationClick = (notification) => {
    if (notification.isUnread) {
      markAsRead(notification.id);
    }
    const route = routeForNotification(notification);
    if (route) {
      setIsNotificationPanelClosing(true);
      setTimeout(() => {
        setIsNotificationPanelOpen(false);
        setIsNotificationPanelClosing(false);
      }, 300);
      navigate(route);
    }
  };

  // Label for the inline deep-link, or null when the notification has no
  // inline link target (task or resident).
  const inlineLinkLabel = (notification) => {
    switch (notification.entityType) {
      case 'Task':
      case 'TaskNote':
        return 'Otvori zadatak';
      case 'Resident':
      case 'ResidentNote':
        return 'Otvori stanara';
      case 'ResidentDocument':
      case 7:
        return 'Otvori dokumente';
      default:
        return null;
    }
  };

  // Close the panel and mark the notification read before following a link.
  const handleNotificationLinkClick = (notification) => {
    if (notification.isUnread) {
      markAsRead(notification.id);
    }
    setIsNotificationPanelClosing(true);
    setTimeout(() => {
      setIsNotificationPanelOpen(false);
      setIsNotificationPanelClosing(false);
    }, 300);
  };

  // Na mobilnom prikazu hamburger zamenjuje „nazad" strelica kada je otvoren
  // profil rezidenta/zaposlenog ili bilo koji side panel (overlay); na desktopu
  // mesto hamburgera uvek zauzima hamburger.
  const showHeaderBack = !isDesktop && shouldShowHeaderBack(location.pathname, isOverlayOpen);

  return (
    <Theme theme={theme}>
      <div 
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => {
          if (isDragging) {
            setIsDragging(false);
            setTouchStart(null);
            setTouchEnd(null);
          }
        }}
        style={{ height: '100%', width: '100%', cursor: isDragging ? 'grabbing' : 'auto' }}
      >
        <Header
          aria-label="Moja aplikacija"
          className="app-header"
          style={{ '--sidenav-width': isDesktop && isSideNavExpanded ? '256px' : '48px' }}
        >
        {showChristmasDecor && (
          <div className={isChristmasFadingOut ? 'christmas-decor-fade-out' : ''}>
            <ChristmasLights />
            <SnowEffect />
          </div>
        )}
        {/* Na mobilnom prikazu mesto hamburgera zauzima „nazad" strelica kada
            je otvoren profil rezidenta/zaposlenog, „detalj" stranica ili bilo
            koji side panel. Na „detalj" stranicama strelica vodi na
            odgovarajuću listu (vidi utils/backNavigation.js), a ako je panel
            otvoren - prvo ga zatvara. U svim ostalim slučajevima tu je
            hamburger koji otvara levi meni; na desktopu je hamburger uvek
            prisutan. */}
        {showHeaderBack ? (
          <HeaderBackButton to={headerBackTarget || undefined} />
        ) : (
          <HeaderMenuButton
            aria-label={isSideNavExpanded ? 'Zatvori meni' : 'Otvori meni'}
            onClick={toggleSideNav}
            isActive={isSideNavExpanded}
          />
        )}

        <HeaderName
          element={Link}
          to="/"
          prefix=""
          style={{ marginLeft: '-8px' }}
          onClick={closeNotificationPanel}
        >
          <img src={logo} alt="Logo" style={{ height: '32px', verticalAlign: 'middle' }} />
        </HeaderName>

        <HeaderGlobalBar>
          <GlobalSearch />
          {/* Ime i prezime prijavljenog korisnika + padajući meni "Moj nalog" (PC) */}
          {isDesktop && <AccountMenu />}
          <HeaderGlobalAction
            aria-label="Obaveštenja"
            onClick={handleNotificationsClick}
            tooltipAlignment="end"
            className="notification-action"
            isActive={isNotificationPanelOpen}
          >
            <NotificationNew size={24} />
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications}</span>
            )}
          </HeaderGlobalAction>
        </HeaderGlobalBar>
      </Header>

      <SideNav
        aria-label="Bočna navigacija"
        isFixedNav
        expanded={isSideNavExpanded}
        onOverlayClick={() => setIsSideNavExpanded(false)}
        isChildOfHeader={false}
        isPersistent={isDesktop}
      >
        {sidenavLoading ? (
          <div className="shell-sidenav-loading">Učitavanje...</div>
        ) : (
        <SideNavItems>
          <SideNavLink
            onClick={() => handleSideNavClick('/')}
            isActive={location.pathname === '/' || 
                     (userRole === 'administrator' && location.pathname === '/admin-dashboard') ||
                     ((userRole === 'nurse' || userRole === 'caretaker') && location.pathname === '/nurse-dashboard') ||
                     (userRole === 'caregiver' && location.pathname === '/caregiver-dashboard') ||
                     (userRole === 'physiotherapist' && location.pathname === '/physiotherapist-dashboard') ||
                     (userRole === 'kitchen' && location.pathname === '/kitchen-dashboard')}
            renderIcon={Home}
          >
            {userRole === 'administrator' ? 'Dashboard' : 'Dashboard'}
          </SideNavLink>

          {/* Dom (administrator) + zajedničke stavke doma, zadatim redosledom:
              Panel doma -> Korisnici -> Sobe -> Dešavanja. */}
          {userRole === 'administrator' && (
            <SideNavLink
              onClick={() => handleSideNavClick('/panel-doma')}
              isActive={location.pathname === '/panel-doma'}
              renderIcon={Building}
            >
              Panel doma
            </SideNavLink>
          )}
          {canSeeHomeItems && canSee('/residents') && (
            <SideNavLink
              onClick={() => handleSideNavClick('/residents')}
              isActive={location.pathname === '/residents' || location.pathname.startsWith('/residents/')}
              renderIcon={User}
            >
              Korisnici
            </SideNavLink>
          )}
          {canSeeHomeItems && canSee('/rooms') && (
            <SideNavLink
              onClick={() => handleSideNavClick('/rooms')}
              isActive={location.pathname === '/rooms'}
              renderIcon={Hotel}
            >
              Sobe
            </SideNavLink>
          )}
          <SideNavLink
            onClick={() => handleSideNavClick('/events')}
            isActive={location.pathname === '/events'}
            renderIcon={CalendarHeatMap}
          >
            Dešavanja
          </SideNavLink>

          {/* Zaposleni - stavke su izdvojene iz grupe, svaka sa svojom ikonicom.
              Vidljivost svake stavke ograđuje njena sopstvena dozvola (npr.
              zaposleni sa samo `shifts.manage` vidi „Raspored smena"). */}
          {canSee('/employees') && (
            <SideNavLink
              onClick={() => handleSideNavClick('/employees')}
              isActive={location.pathname === '/employees' || location.pathname.startsWith('/employees/')}
              renderIcon={UserMultiple}
            >
              Zaposleni
            </SideNavLink>
          )}
          {canSee('/shift-management') && (
            <SideNavLink
              onClick={() => handleSideNavClick('/shift-management')}
              isActive={location.pathname === '/shift-management'}
              renderIcon={Time}
            >
              Raspored smena
            </SideNavLink>
          )}
          {/* Negovateljske role imaju svoju stranicu zadataka, ostali „/tasks". */}
          {canSee('/tasks') && (
            <SideNavLink
              onClick={() => handleSideNavClick(isCareRole ? tasksPath : '/tasks')}
              isActive={
                isCareRole
                  ? ['/caregiver-tasks', '/physiotherapist-tasks', '/nurse-tasks'].some(
                      (path) => location.pathname === path
                    )
                  : location.pathname === '/tasks'
              }
              renderIcon={Task}
            >
              Zadaci
            </SideNavLink>
          )}

          {/* „Organizacija" - samostalna stavka, VAN grupe „Zaposleni".
              Vidljiva je onome ko sme da vidi bilo koji njen tab: podešavanja
              oblasti/tipova smena, grupe ili aktivnosti. */}
          {(canSeeOrganizationSettings({ canAny, permissions, isAdmin }) ||
            canSee('/residents/groups') ||
            canSee('/activities')) && (
            <SideNavLink
              onClick={() => handleSideNavClick('/settings')}
              isActive={
                location.pathname === '/settings' ||
                location.pathname === '/residents/groups' ||
                location.pathname === '/activities'
              }
              renderIcon={Grid}
            >
              Organizacija
            </SideNavLink>
          )}

          <SideNavMenu 
            title="Kuhinja" 
            renderIcon={Restaurant}
            onClickCapture={(e) => handleMenuClick(e, 'Kuhinja')}
          >
            {canSee('/jelovnik') && (
              <SideNavMenuItem
                onClick={() => handleSideNavClick('/jelovnik')}
                isActive={location.pathname === '/jelovnik'}
              >
                Jelovnik
              </SideNavMenuItem>
            )}
            {canSee('/meals') && (
              <SideNavMenuItem
                onClick={() => handleSideNavClick('/meals')}
                isActive={location.pathname === '/meals'}
              >
                Obroci
              </SideNavMenuItem>
            )}
            {canSee('/kuhinja-administracija') && (
              <SideNavMenuItem
                onClick={() => handleSideNavClick('/kuhinja-administracija')}
                isActive={location.pathname === '/kuhinja-administracija'}
              >
                Administracija kuhinje
              </SideNavMenuItem>
            )}
          </SideNavMenu>

          <SideNavLink
            onClick={() => handleSideNavClick('/profile')}
            isActive={location.pathname === '/profile'}
            renderIcon={User}
            style={{ marginTop: 'auto', borderTop: '1px solid var(--cds-border-subtle, #e0e0e0)' }}
          >
            Moj nalog
          </SideNavLink>

          <SideNavLink
            onClick={() => handleSideNavClick('/blog')}
            isActive={location.pathname === '/blog'}
            renderIcon={Blog}
          >
            Naš blog
          </SideNavLink>

          <SideNavLink
            onClick={() => handleSideNavClick('/help')}
            isActive={location.pathname === '/help'}
            renderIcon={Help}
          >
            Pomoć
          </SideNavLink>
        </SideNavItems>
        )}
      </SideNav>

      {/* Notification Panel */}
      {isNotificationPanelOpen && (
        <>
          <div 
            className={`notification-overlay ${isNotificationPanelClosing ? 'closing' : ''}`}
            onClick={() => {
              setIsNotificationPanelClosing(true);
              setTimeout(() => {
                setIsNotificationPanelOpen(false);
                setIsNotificationPanelClosing(false);
              }, 300);
            }}
          />
          <div className={`notification-panel ${isNotificationPanelClosing ? 'closing' : ''}`}>
            <div className="notification-panel-header">
              <h3>Obaveštenja</h3>
              {unreadNotifications > 0 && (
                <button 
                  className="mark-all-read-btn"
                  onClick={handleMarkAllAsRead}
                >
                  Označi sve kao pročitano
                </button>
              )}
            </div>
            <div className="notification-panel-content">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <NotificationNew size={32} />
                  <p>Nema novih obaveštenja</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <div 
                    key={notification.id}
                    className={`notification-item ${notification.isUnread ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                  >
                    <div className="notification-content">
                      <h4>{notification.title}</h4>
                      <p>{notification.body}</p>
                      <span className="notification-time">
                        {formatNotificationTime(notification.createdAt)}
                      </span>
                      {inlineLinkLabel(notification) && notification.entityId && (
                        <Link
                          to={routeForNotification(notification)}
                          className="notification-task-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationLinkClick(notification);
                          }}
                        >
                          {inlineLinkLabel(notification)}
                        </Link>
                      )}
                    </div>
                    <div className="notification-actions">
                      {notification.isUnread && (
                        <button
                          className="notification-action-btn"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                          title="Označi kao pročitano"
                        >
                          <View size={16} />
                        </button>
                      )}
                      <button
                        className="notification-action-btn delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteNotification(notification.id); }}
                        title="Obriši"
                      >
                        <Close size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <Content style={{ background: theme === 'white' ? '#f4f4f4' : '#161616' }}>
        {/* Dok se auth stanje ne utvrdi, Shell (header + levi sidebar) ostaje
            vidljiv, ali stranicu ne renderujemo - tako se izbegava poziv API-ja
            bez tokena i ružni "prazan" sadržaj. */}
        {!authLoading && <Outlet />}
      </Content>

      <ScrollToTopButton />

      {/* Na Androidu sistemsko „nazad" prvo zatvara otvoren panel/modal,
          umesto da odmah ide na prethodnu stranicu. */}
      <AndroidBackGuard />
      </div>
    </Theme>
  );
};

export default Shell;
